import Field from '@components/field';
import Group from '@components/group';
import Icon from '@components/icon';
import Select from '@components/select';
import { observer } from 'mobx-react-lite';
import { FC } from 'react';

import { Checkbox } from 'components/Checkbox';
import { PlusButton } from 'components/PlusButton';
import { QuestionTooltip } from 'components/QuestionTooltip';
import { Stack } from 'components/Stack';
import { notificationsFaq, subscriptionStats } from 'constants/pages';
import { useStore } from 'hooks/useStore';
import { AdditionalNotificationTypes } from 'store/OptionsStore';
import { PopupIds } from 'store/UIStore/Popups';
import { interpolate } from 'utils/string/format';

import { MobileAppChannelCheckBox } from './MobileAppChannelCheckBox';
import { Prices } from './Prices';
import { SMS } from './SMS';
import { Trial } from './Trial';

import css from './SubsSettings.module.css';

const SubsSettings: FC = () => {
  const {
    app: { i18n, env },
    savedFilters: { popup },
    profile,
    ui: { popups },
  } = useStore();

  if (!popup) return null;

  const {
    channels: { site, email, sms, isOneChannelSelected },
  } = popup;

  const smsLabel = `${i18n.filterPopup.sms} ${popup?.smsParams?.rate} ${i18n.app.declensions.atis.few}`;

  return (
    <>
      <div className={css.divider} />

      <div className={css.contact}>{profile.data?.profile?.contact.name}</div>

      <div className={css.channels}>
        <Checkbox
          checked={site.value}
          disabled={site.value && isOneChannelSelected}
          tooltip={site.value && isOneChannelSelected && i18n.subscriptions.channels.disabledTooltip}
          tooltipProps={{ maxWidth: 270 }}
          onChange={site.setValueFromCheckbox}
        >
          {i18n.filterPopup.onSite}
        </Checkbox>

        {popup?.contactEmail ? (
          <Checkbox
            checked={email.value}
            disabled={email.value && isOneChannelSelected}
            tooltip={email.value && isOneChannelSelected && i18n.subscriptions.channels.disabledTooltip}
            tooltipProps={{ maxWidth: 270 }}
            onChange={email.setValueFromCheckbox}
          >
            {i18n.filterPopup.email}
          </Checkbox>
        ) : (
          <PlusButton
            className={css.noEmailBtn}
            text={i18n.filterPopup.email}
            onClick={() => popups.open(PopupIds.editContactPopup)}
          />
        )}

        <MobileAppChannelCheckBox />

        {popup?.smsParams?.phoneForSms ? (
          <Stack gap={2}>
            <Checkbox
              checked={sms.value}
              disabled={sms.value && isOneChannelSelected}
              tooltip={sms.value && isOneChannelSelected && i18n.subscriptions.channels.disabledTooltip}
              tooltipProps={{ maxWidth: 270 }}
              onChange={sms.setValueFromCheckbox}
            >
              {smsLabel}
            </Checkbox>

            <span className={css.tel}>{popup?.smsParams?.phoneForSms}</span>
            <div className={css.stats}>
              <Icon color="var(--blue)" height={10} name="ic_charts" width={10} />{' '}
              <a
                className="glz-link glz-is-medium glz-is-underline"
                href={interpolate(subscriptionStats, { env })}
                rel="noreferrer"
                target="_blank"
              >
                {i18n.filterPopup.statistics}
              </a>
            </div>
          </Stack>
        ) : (
          <PlusButton text={i18n.filterPopup.telephone} onClick={() => popups.open(PopupIds.editContactPopup)} />
        )}
      </div>

      <Group displayColumn>
        <Field className={css.additionalNotifications} size="xxlarge">
          <Select
            options={popup.additionalNotifications.options}
            value={popup.additionalNotifications.option}
            onChange={popup.additionalNotifications.setOption}
          />

          {popup.additionalNotifications.option.value === AdditionalNotificationTypes.rateRased && (
            <QuestionTooltip text={i18n.filterPopup.informRateUpVerbose} />
          )}
        </Field>

        {!popup.hasOnlyPersonalBoards && !profile.hasLoadContacts && (
          <Field size="xxlarge">
            <Checkbox
              checked={popup.isOnlyAvailableContacts.value}
              onChange={popup.isOnlyAvailableContacts.setValueFromCheckbox}
            >
              {i18n.filterPopup.closedContacts}

              <QuestionTooltip text={i18n.filterPopup.closedContactsVerbose} tooltipWidth={170} />
            </Checkbox>
          </Field>
        )}
      </Group>

      <Stack className={css.info} gap={8}>
        <Trial mode="full" />

        {profile.hasUnlimitedNotificationsLicense && (
          <>
            <div className={css.infoItem}>{i18n.filterPopup.unlimitedActive}</div>
            <SMS className={css.sms} />
          </>
        )}

        <Prices />

        <a className="glz-link glz-is-medium glz-is-underline" href={notificationsFaq} rel="noreferrer" target="_blank">
          {i18n.filterPopup.moreAbout}
          <Icon className="glz-after-icon" color="currentColor" height={14} name="ic_external" width={9} />
        </a>
      </Stack>
    </>
  );
};

const connected = observer(SubsSettings);

export { connected as SubsSettings };
